<template>
  <content-modal
    class="message-modal assignment-edit-modal"
    :visible="visible"
    :close-aria-label="closeAriaLabel"
    :size="size"
    :use-state-button="true"
    @modal-shown="actionShown"
    @modal-hidden="actionHidden"
    :auto-hide-off="true"
    @modal-hide-request="actionHideRequest"
  >
    <template v-if="use_title" #title>
      {{ t("assignment_edit_modal_confirmation_title") }}
    </template>
    <template v-if="use_content" #content>
      <modal-content>
        <span class="info">{{ t("assignment_edit_modal_confirmation_subtitle") }}</span>
        <div class="description">
          <h5 class="title">
            <svg-icon name="Info32" kind="indicative" />
            <span>{{ t("assignment_edit_modal_note") }}</span>
          </h5>
        </div>

        <cv-list>
          <cv-list-item>
            <h6>{{ t("assignment_edit_modal_linked_assignments_title") }}</h6>
            <span>
              {{ t("assignment_edit_modal_linked_assignments_subtitle") }}
            </span>
          </cv-list-item>
          <cv-list-item>
            <h6>{{ t("assignment_edit_modal_access_title") }}</h6>
            <span>
              {{ t("assignment_edit_modal_access_subtitle") }}
            </span>
          </cv-list-item>
          <cv-list-item>
            <h6>{{ t("assignment_edit_modal_progress_title") }}</h6>
            <span>
              {{ t("assignment_edit_modal_progress_subtitle") }}
            </span>
          </cv-list-item>
          <cv-list-item v-if="hasGamification">
            <h6>{{ t("assignment_edit_modal_gamification_title") }}</h6>
            <span>
              {{ t("assignment_edit_modal_gamification_subtitle") }}
            </span>
          </cv-list-item>
        </cv-list>
        <template #tools>
          <cv-button
            id="button_cancel"
            class="cancel"
            kind="secondary"
            size="field"
            @click="actionSecondary"
            >{{ t("assignment_edit_and_resume_modal_button_cancel") }}</cv-button
          >
          <fetch-data-button
            id="button_assignment_edit"
            kind="primary"
            size="field"
            :error="dataEditButton.error"
            :loading="dataEditButton.loading"
            :errorMessage="dataEditButton.errorMessage"
            @click="actionPrimary"
            >{{ t("assignment_edit_and_resume_modal_button_ok") }}</fetch-data-button
          >
        </template>
      </modal-content>
    </template>
  </content-modal>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import modalEventBus from "@plugins/modal/service/event-bus";
import { useVm } from "@utils/vm.ts";

interface Props {
  params?: {
    hasGamification?: boolean;
    actionPrimary?: () => void;
    actionSecondary?: () => void;
  } | null;
}

const props = defineProps<Props>();

const { t } = useI18n();
const { vmNode } = useVm();

const closeAriaLabel = ref("Close");
const use_title = ref(true);
const use_content = ref(true);
const size = ref("small");
const visible = ref(true);
const dataEditButton = ref({
  kind: "primary",
  error: false,
  loading: false,
  errorMessage: t("api_action_generic_error_message"),
});

const hasGamification = computed(() => {
  return props.params?.hasGamification;
});

const actionShown = () => {
  visible.value = true;
};

const actionHidden = () => {
  visible.value = false;
  modalEventBus.$emit("modal-hidden", vmNode.key);
};

const actionHideRequest = (event: object) => {
  // to prevent close modal upon clicking anywhere outside the modal container
  if (event["cv:reason"] !== "external-click") {
    actionHidden();
  }
};

const actionSecondary = () => {
  if (props.params?.actionSecondary && typeof props.params.actionSecondary === "function") {
    props.params.actionSecondary();
  }
  visible.value = false;
};

const actionPrimary = () => {
  props.params?.actionPrimary?.();
  visible.value = false;
};

onMounted(() => {
  visible.value = true;
});
</script>
